import { useState, useCallback, useEffect } from 'react'
import { useEditorStore } from '../../store/editor.store'
import { buildCssVars } from '../../engine/styleEngine'
import { CanvasSection } from './CanvasSection'
import { GlobalSection, GlobalSectionToggle } from './GlobalSection'
import { DeviceContext } from '../../contexts/DeviceContext'
import { SectionPresetsModal } from './SectionPresetsModal'
import { CanvasComments } from '../CanvasComments'
import { DESIGN_STYLE_MAP } from '../../data/designStyles'
import { buildSiteThemeCss, googleFontsHref, parseThemeConfig, isDesignSystemV2, CORE_BLOCKS_CSS } from '@bst/blocks'

interface CanvasProps {
  commentsEnabled?: boolean
  siteId?: string | null
}

function linkLabel(href: string): string {
  if (!href || href === '#') return '— Ссылка не настроена'
  if (href.startsWith('#')) return `↓ К секции: ${href}`
  if (href.startsWith('http://') || href.startsWith('https://')) return `↗ Внешняя ссылка: ${href}`
  return `→ Страница: ${href}`
}

export function Canvas({ commentsEnabled = false, siteId }: CanvasProps) {
  const { page, device, zoom, showGrid, setZoom, selectBlock, selectSection, globalHeader, globalFooter } =
    useEditorStore()

  // Design System tokens → CSS vars
  const designTokens = useEditorStore(s => s.designTokens)

  useEffect(() => {
    const id = 'bst-ds-tokens'
    if (!designTokens.length) {
      document.getElementById(id)?.remove()
      return
    }
    const vars = buildCssVars(designTokens)
    const css = `:root { ${Object.entries(vars).map(([k, v]) => `${k}: ${v};`).join(' ')} }`
    let el = document.getElementById(id) as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = id
      document.head.appendChild(el)
    }
    el.textContent = css
    return () => { document.getElementById(id)?.remove() }
  }, [designTokens])

  // Targeted selector — only re-renders when designStyle actually changes
  const designStyle = useEditorStore(s => s.settings.designStyle)
  const activeStyle = designStyle ? DESIGN_STYLE_MAP[designStyle] : null
  const styleVars = activeStyle ? activeStyle.cssVars : {}

  const [showPresets, setShowPresets] = useState(false)

  // Site theme (AI design system) → canvas. Same CSS as the published site,
  // scoped to the canvas so it never touches the editor UI variables.
  const themeConfig = useEditorStore(s => s.settings.themeConfig)
  const designV2 = isDesignSystemV2(themeConfig)
  useEffect(() => {
    const styleId = 'bst-site-theme'
    const linkId = 'bst-site-theme-fonts'
    const t = parseThemeConfig(themeConfig)
    // Only design-system-v2 sites (AI generated): legacy sites keep the old canvas look
    if (!t || !designV2) {
      document.getElementById(styleId)?.remove()
      document.getElementById(linkId)?.remove()
      return
    }
    const css = buildSiteThemeCss(t, '.bst-canvas-site') + '\n' + CORE_BLOCKS_CSS
    let el = document.getElementById(styleId) as HTMLStyleElement | null
    if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el) }
    el.textContent = css
    const href = googleFontsHref(t)
    let link = document.getElementById(linkId) as HTMLLinkElement | null
    if (href) {
      if (!link) { link = document.createElement('link'); link.id = linkId; link.rel = 'stylesheet'; document.head.appendChild(link) }
      if (link.href !== href) link.href = href
    } else link?.remove()
    return () => { document.getElementById(styleId)?.remove(); document.getElementById(linkId)?.remove() }
  }, [themeConfig, designV2])

  useEffect(() => {
    const styleId = 'bst-design-style'
    if (!activeStyle) {
      document.getElementById(styleId)?.remove()
      return
    }
    const fontImports = (activeStyle.googleFonts ?? [])
      .map(f => `@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(f)}&display=swap');`)
      .join('\n')
    const css = `${fontImports}

/* ── Base canvas ─────────────────────────────────────────────── */
.bst-style-root {
  background: var(--bst-page-bg, transparent);
  font-family: var(--bst-font-body, inherit);
  --accent: var(--bst-accent, #5b6af0);
}

/* ── Typography ──────────────────────────────────────────────── */
.bst-style-root h1,.bst-style-root h2,.bst-style-root h3,
.bst-style-root h4,.bst-style-root h5,.bst-style-root h6 {
  font-family: var(--bst-font-heading, inherit) !important;
  font-weight: var(--bst-heading-weight, bold) !important;
  letter-spacing: var(--bst-letter-spacing, normal) !important;
  color: var(--bst-heading-color, inherit) !important;
}
.bst-style-root p,.bst-style-root li {
  font-family: var(--bst-font-body, inherit);
}

/* ── Buttons ─────────────────────────────────────────────────── */
.bst-style-root button {
  background: var(--bst-btn-bg) !important;
  color: var(--bst-btn-color) !important;
  border: var(--bst-border) !important;
  border-radius: var(--bst-r) !important;
  box-shadow: var(--bst-shadow) !important;
  font-family: var(--bst-font-body, inherit) !important;
}

/* ── Border radius ───────────────────────────────────────────── */
.bst-style-root div[style*="border-radius:"],
.bst-style-root section[style*="border-radius:"],
.bst-style-root article[style*="border-radius:"],
.bst-style-root a[style*="border-radius:"],
.bst-style-root input,
.bst-style-root select,
.bst-style-root textarea {
  border-radius: var(--bst-r) !important;
}

/* ── Box shadow ──────────────────────────────────────────────── */
.bst-style-root div[style*="box-shadow:"],
.bst-style-root section[style*="box-shadow:"] {
  box-shadow: var(--bst-shadow) !important;
}

/* ── Card backgrounds ────────────────────────────────────────── */
.bst-style-root div[style*="border-radius:"][style*="background:"],
.bst-style-root article[style*="border-radius:"][style*="background:"] {
  background: var(--bst-card-bg) !important;
}

/* ── Light section backgrounds ───────────────────────────────── */
.bst-style-root div[style*="background: rgb(248, 249, 250)"],
.bst-style-root div[style*="background: rgb(243, 244, 246)"],
.bst-style-root div[style*="background: rgb(249, 250, 251)"],
.bst-style-root div[style*="background: rgb(245, 245, 245)"],
.bst-style-root div[style*="background: rgb(250, 250, 250)"],
.bst-style-root div[style*="background: rgb(255, 255, 255)"] {
  background: var(--bst-section-light-bg, var(--bst-page-bg, inherit)) !important;
}

/* ── Borders ─────────────────────────────────────────────────── */
.bst-style-root div[style*="border-radius:"][style*="border:"] {
  border: var(--bst-border) !important;
}

/* ── Form inputs ─────────────────────────────────────────────── */
.bst-style-root input, .bst-style-root textarea, .bst-style-root select {
  border: var(--bst-border) !important;
  border-radius: var(--bst-r) !important;
  font-family: var(--bst-font-body, inherit) !important;
}

/* ── Accent color overrides (#5b6af0 = rgb(91, 106, 240)) ────── */
.bst-style-root [style*="color: rgb(91, 106, 240)"] {
  color: var(--bst-accent, #5b6af0) !important;
}
.bst-style-root [style*="background: rgb(91, 106, 240)"],
.bst-style-root [style*="background-color: rgb(91, 106, 240)"] {
  background: var(--bst-accent, #5b6af0) !important;
  background-color: var(--bst-accent, #5b6af0) !important;
}
.bst-style-root [style*="border"][style*="rgb(91, 106, 240)"] {
  border-color: var(--bst-accent, #5b6af0) !important;
}
.bst-style-root [style*="fill: rgb(91, 106, 240)"] {
  fill: var(--bst-accent, #5b6af0) !important;
}
.bst-style-root [style*="stroke: rgb(91, 106, 240)"] {
  stroke: var(--bst-accent, #5b6af0) !important;
}`
    let el = document.getElementById(styleId) as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = styleId
      document.head.appendChild(el)
    }
    el.textContent = css
    return () => { document.getElementById(styleId)?.remove() }
  }, [activeStyle?.id])

  const [linkTooltip, setLinkTooltip] = useState<{ x: number; y: number; href: string } | null>(null)

  const handleLinkMove = useCallback((e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest('[data-link]') as HTMLElement | null
    if (!el) { setLinkTooltip(null); return }
    setLinkTooltip({ x: e.clientX, y: e.clientY, href: el.dataset.link ?? '' })
  }, [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return
    e.preventDefault()
    const delta = e.deltaY > 0 ? -10 : 10
    setZoom(zoom + delta)
  }, [zoom, setZoom])

  const widthMap = { desktop: '100%', tablet: '768px', mobile: '390px' }
  const canvasWidth = widthMap[device]

  return (
    <>
      {linkTooltip && (
        <div style={{
          position: 'fixed', top: linkTooltip.y + 14, left: linkTooltip.x + 8,
          background: '#1e293b', color: '#f1f5f9', padding: '5px 10px',
          borderRadius: '6px', fontSize: '12px', zIndex: 99999,
          pointerEvents: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          maxWidth: '320px', whiteSpace: 'nowrap',
        }}>
          {linkLabel(linkTooltip.href)}
        </div>
      )}
      <div
        className="editor-canvas"
        onClick={() => { selectBlock(null); selectSection(null) }}
        onWheel={handleWheel}
        onMouseMove={handleLinkMove}
        onMouseLeave={() => setLinkTooltip(null)}
        style={{ position: 'relative', ...(showGrid ? {
          backgroundImage: 'radial-gradient(circle, rgba(91,106,240,0.32) 1.5px, transparent 1.5px)',
          backgroundSize: `${20 * zoom / 100}px ${20 * zoom / 100}px`,
        } : {}) }}
      >
        <CanvasComments siteId={siteId ?? null} enabled={commentsEnabled} />
        <div
          style={{
            transform: zoom !== 100 ? `scale(${zoom / 100})` : undefined,
            transformOrigin: 'top center',
            minHeight: zoom !== 100 ? `${100 / (zoom / 100)}%` : undefined,
          }}
        >
          <DeviceContext.Provider value={device}>
            <div
              className={[activeStyle ? 'bst-style-root' : '', designV2 ? 'bst-canvas-site' : ''].filter(Boolean).join(' ') || undefined}
              style={{ width: canvasWidth, margin: '0 auto', paddingBottom: '80px', ...styleVars } as React.CSSProperties}
            >
              {/* Global Header */}
              {globalHeader
                ? <GlobalSection target="header" section={globalHeader} />
                : <GlobalSectionToggle target="header" />
              }

              {/* Page sections */}
              {page.sections.length === 0 ? (
                <div className="canvas-empty-state">
                  <div className="empty-icon">✦</div>
                  <h2>Начните добавлять блоки</h2>
                  <p>Выберите блок из панели слева или перетащите его на холст</p>
                  <button className="btn-primary btn-lg" onClick={(e) => { e.stopPropagation(); setShowPresets(true) }}>
                    + Добавить секцию
                  </button>
                </div>
              ) : (
                <>
                  {page.sections.map((section) => (
                    <CanvasSection key={section.id} section={section} device={device} />
                  ))}
                  <div style={{ textAlign: 'center', paddingTop: '24px' }}>
                    <button className="btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setShowPresets(true) }}>
                      + Добавить секцию
                    </button>
                  </div>
                </>
              )}

              {/* Global Footer */}
              {globalFooter
                ? <GlobalSection target="footer" section={globalFooter} />
                : <GlobalSectionToggle target="footer" />
              }
            </div>
          </DeviceContext.Provider>
        </div>
      </div>

      {showPresets && <SectionPresetsModal onClose={() => setShowPresets(false)} />}
    </>
  )
}
